import { EmptyState, FlexColumn, Loading } from '@gorila-shared-ui/components';
import { Accordion, Panel } from 'baseui/accordion';
import { useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getStreamingMedia } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  activeEvidenceState,
  evidenceAlarmFilterState,
  evidenceMediaTypeFilterState,
  evidenceOriginFilterState,
  loadingEvidenceState,
  reportEndDateState,
  reportEvidenceStates,
  reportSelectedAssetIdsState,
  reportStartDateState,
} from '../../../storage/reports';
import { EvidenceMedia } from '../../../types/evidences';
import { StreamingMediaLibraryFromAsset } from './StreamingMediaLibraryFromAsset';
import StreamingMediaReportTitle from './StreamingMediaReportTitle';

function StreamingMediaReport() {
  const { css } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const [isloadingEvidenceState, setIsLoadingEvidenceState] = useRecoilState(loadingEvidenceState);
  const selectedAssetIds = useRecoilValue(reportSelectedAssetIdsState);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const mediaType = useRecoilValue(evidenceMediaTypeFilterState);
  const origin = useRecoilValue(evidenceOriginFilterState);
  const alarmType = useRecoilValue(evidenceAlarmFilterState);
  const setActiveReport = useSetRecoilState(activeEvidenceState);
  const setReportHistorics = useSetRecoilState(reportEvidenceStates);
  const resetReportHistorics = useResetRecoilState(reportEvidenceStates);

  useEffect(() => {
    return () => {
      resetReportHistorics();
    };
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [apiParams?.clientId, apiParams?.subClientId, selectedAssetIds, startDate, endDate, mediaType, origin, alarmType]);

  const loadLibrary = async () => {
    if (!selectedAssetIds?.length || !startDate || !endDate || !apiParams) return;

    const promises = selectedAssetIds.map((assetId) =>
      getStreamingMedia(
        apiParams.clientId,
        assetId,
        startDate,
        endDate,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
        mediaType,
        origin,
        alarmType,
      ),
    );

    const loadHistoricEvidence = async () => {
      setIsLoadingEvidenceState(true);
      const responses = await Promise.allSettled(promises);

      const newHistorics: { [key: string]: EvidenceMedia[] } = {};
      responses.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const {
            value: { data, error },
          } = result;
          if (!error) {
            newHistorics[selectedAssetIds[index]] = data ?? [];
          }
        }
      });
      setReportHistorics(newHistorics);

      if (newHistorics[selectedAssetIds[0]]) {
        setActiveReport(selectedAssetIds[0]);
      }
      setIsLoadingEvidenceState(false);
    };

    loadHistoricEvidence();
  };

  if (isloadingEvidenceState) {
    return <Loading />;
  }

  if (!selectedAssetIds?.length) {
    return <EmptyState title="Selecciona un activo para visualizar el reporte." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flex: 1, overflow: 'hidden' })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <div style={{ width, height, overflow: 'auto' }}>
            <Accordion
              key={selectedAssetIds[0]}
              initialState={{ expanded: [selectedAssetIds[0]] }}
              onChange={({ expanded }) => setActiveReport(expanded.length ? (expanded[0] as string) : undefined)}
            >
              {selectedAssetIds.map((assetId) => (
                <Panel
                  key={assetId}
                  title={<StreamingMediaReportTitle assetId={assetId} />}
                  overrides={{
                    Content: {
                      style: {
                        paddingTop: 0,
                      },
                    },
                  }}
                >
                  <StreamingMediaLibraryFromAsset assetId={assetId} />
                </Panel>
              ))}
            </Accordion>
          </div>
        )}
      </AutoSizer>
    </FlexColumn>
  );
}

export default StreamingMediaReport;
